import { GridFilterModel, GridSortModel, useGridApiRef } from '@mui/x-data-grid';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { decodeJson, encodeJson, getParams, setParams } from '../utils/gridUrlState';
import { buildQuickFilterItems } from '../utils/quickFilters';

type Options = {
  apiRef: ReturnType<typeof useGridApiRef>;
  selected: string[];
  setSelected: (vals: string[]) => void;
  dateRange: [string, string];
  setCustom: (from: Date, to: Date) => void;
  replaceHistory?: boolean;
  debounceMs?: number;
};

export const useGridStateUrlSync = ({
  apiRef,
  selected,
  setSelected,
  dateRange,
  setCustom,
  replaceHistory = true,
  debounceMs = 300
}: Options) => {
  const navigate = useNavigate();
  const location = useLocation();
  const applyingRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Initial load from URL
  useEffect(() => {
    const params = getParams(location.search);
    const fm = decodeJson<GridFilterModel>(params.fm);
    const sm = decodeJson<GridSortModel>(params.sm);

    applyingRef.current = true;

    if (fm) apiRef.current.setFilterModel(fm);
    if (sm) apiRef.current.setSortModel(sm);

    if (params.s) {
      const chips = params.s.split(',').filter(Boolean);
      setSelected(chips);
      if (!fm) {
        apiRef.current.setFilterModel({
          items: buildQuickFilterItems(chips),
          quickFilterValues: []
        });
      }
    }

    if (params.df && params.dt) {
      setCustom(new Date(params.df), new Date(params.dt));
    }

    const t = setTimeout(() => {
      applyingRef.current = false;
    }, 100);
    return () => clearTimeout(t);
  }, []); // Run only once on mount

  // Function to write state to URL
  const writeToUrl = useMemo(
    () => (next: Partial<ReturnType<typeof getParams>>) => {
      if (applyingRef.current) {
        console.log('⏭️ Skipping URL update (applying from URL)');
        return;
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        const currentParams = getParams(location.search);
        const mergedParams = { ...currentParams, ...next };
        const newSearch = setParams('', mergedParams);

        console.log('📝 Updating URL:', {
          current: location.search,
          new: newSearch
        });

        if (location.search !== newSearch) {
          navigate({ search: newSearch }, { replace: replaceHistory, preventScrollReset: true });
        }
      }, debounceMs);
    },
    [location.search, navigate, replaceHistory, debounceMs]
  );

  // Subscribe to filter changes
  useEffect(() => {
    const api = apiRef.current;

    const unsubFilter = api.subscribeEvent('filterModelChange', (model) => {
      console.log('🔍 Filter changed:', model);
      writeToUrl({ fm: encodeJson(model) });

      const q =
        Array.isArray(model.quickFilterValues) && model.quickFilterValues.length
          ? model.quickFilterValues.join(' ')
          : '';

      if (q) {
        writeToUrl({ q });
      }
    });

    const unsubSort = api.subscribeEvent('sortModelChange', (model) => {
      console.log('↕️ Sort changed:', model);
      writeToUrl({ sm: encodeJson(model) });
    });

    return () => {
      unsubFilter();
      unsubSort();
    };
  }, [apiRef, writeToUrl]);

  // Sync selected chips to URL
  useEffect(() => {
    if (!applyingRef.current) {
      console.log('🏷️ Selected changed:', selected);
      writeToUrl({ s: selected.join(',') });
    }
  }, [selected, writeToUrl]);

  // Sync date range to URL
  useEffect(() => {
    if (!applyingRef.current) {
      const [df, dt] = dateRange;
      console.log('📅 Date range changed:', { df, dt });
      writeToUrl({ df, dt });
    }
  }, [dateRange, writeToUrl]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);
};
